import { Input, InputNumber, Select } from '@aigc/antd';
import { AiDrawer } from '@aigc/components';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlayRef } from '@aigc/overlay-hook';
import { PlusOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Form, Space, Upload } from 'antd';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { array, number, object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import { createBeforeUpload, createUploadValidator } from '../../file';
import { usePreviewFn } from '../../file/hooks';
import type { UploadFileOutputDto } from '../../file/model';
import { fileSchema } from '../../file/schema';
import { toFileListFormValues, toFileListUrls } from '../../file/utils';
import { HookTypeSelect } from '../../lookup/components/hook-type-select';
import { ScriptLanguageSelect } from '../../lookup/components/script-language-select';
import { SellingPointSelect } from '../../lookup/components/selling-point-select';
import { tagsQueryOptions } from '../../tag/api';
import { AspectRatioSelect } from '../../video/components/aspect-ratio-select';
import {
  createScriptTemplateMutationOptions,
  getScriptTemplateQueryOptions,
  updateScriptTemplateMutationOptions,
} from '../api';
import type {
  ScriptTemplateCreateDto,
  ScriptTemplateUpdateDto,
} from '../model';

const schema = object({
  video_url: fileSchema.length(1, '请上传一个视频').required(),
  video_duration: number()
    .nullable()
    .default(null)
    .when('mode', {
      is: 'update',
      then: (s) => s.required(),
    }),
  video_aspect_ratio: string()
    .nullable()
    .default(null)
    .when('mode', {
      is: 'update',
      then: (s) => s.required(),
    }),
  reference_image_urls: fileSchema.max(5).meta({ unit: '张' }),
  product_name: string().trim().required('请输入产品名称'),
  tags: array(string().trim().required()).default([]),
  prompt: string().trim().nullable().required(),
  card_result: string().trim().nullable().default(null),
  hook_type_id: number().nullable().default(null),
  selling_point_ids: array(number().required()).default([]),
  script_language_id: number().nullable().default(null),
  remark: string().trim().nullable().default(null),
});

export const ScriptTemplateFormDrawer = (props: {
  script_template_id?: number;
}) => {
  const onPreview = usePreviewFn();
  const overlay_ref = useOverlayRef<boolean>();
  const { message, notification } = App.useApp();
  const is_edit = !!props.script_template_id;

  const { control, handleSubmit, setValues } = useForm({
    defaultValues: {
      video_url: [],
      reference_image_urls: [],
      product_name: '',
      tags: [],
      prompt: '',
      card_result: null,
      hook_type_id: null,
      selling_point_ids: [],
      script_language_id: null,
      remark: null,
      video_aspect_ratio: null,
      video_duration: null,
    },
    resolver: yupResolver(schema),
    context: {
      mode: is_edit ? 'update' : 'create',
    },
  });

  const { data: detail, isFetching: is_detail_fetching } = useQuery({
    ...getScriptTemplateQueryOptions(props.script_template_id ?? 0),
    enabled: is_edit,
  });

  const { data: tags = [] } = useQuery(tagsQueryOptions);
  useEffect(() => {
    if (!detail) {
      return;
    }

    setValues({
      video_url: toFileListFormValues([detail.video_url]),
      reference_image_urls: toFileListFormValues(
        detail.reference_image_urls ?? [],
      ),
      product_name: detail.product_name,
      tags: detail.tags.map((item) => item.id),
      prompt: detail.prompt,
      card_result: detail.card_result,
      hook_type_id: detail.hook_type_id,
      selling_point_ids: detail.selling_points.map((item) => item.id),
      script_language_id: detail.script_language_id,
      remark: detail.remark,
      video_duration: is_edit ? detail.video_duration : void 0,
      video_aspect_ratio: is_edit ? detail.video_aspect_ratio : void 0,
    });
  }, [detail, is_edit, setValues]);

  const create_script_template = useMutation(
    createScriptTemplateMutationOptions,
  );
  const update_script_template = useMutation(
    updateScriptTemplateMutationOptions,
  );

  const is_submitting =
    create_script_template.isPending || update_script_template.isPending;

  const handle_close = () => {
    overlay_ref.close();
  };

  const onSubmit = handleSubmit(async (values) => {
    const common_payload: ScriptTemplateCreateDto | ScriptTemplateUpdateDto = {
      video_url: toFileListUrls(values.video_url).at(0)!,
      reference_image_urls: toFileListUrls(values.reference_image_urls),
      prompt: values.prompt,
      product_name: values.product_name,
      tags: values.tags,
      card_result: values.card_result,
      hook_type_id: values.hook_type_id,
      selling_point_ids: values.selling_point_ids,
      script_language_id: values.script_language_id,
      remark: values.remark,
    };

    if (props.script_template_id) {
      await update_script_template.mutateAsync({
        id: props.script_template_id,
        data: {
          ...common_payload,
          video_duration: values.video_duration,
          video_aspect_ratio: values.video_aspect_ratio,
        },
      });
      message.success('脚本模版已更新');
    } else {
      await create_script_template.mutateAsync(common_payload);
      overlay_ref.close(true);
      message.success('脚本模版已创建');
    }
  });

  const acceptVideo = useAccept(['video/*']);
  useAutoPaste('#videoUploader', {
    filter: acceptVideo.filter,
  });

  const acceptImage = useAccept(['.jpg', '.jpeg', '.png', '.webp']);
  useAutoPaste('#imageUploader', {
    filter: acceptImage.filter,
  });

  return (
    <AiDrawer
      title={is_edit ? '编辑脚本模版' : '新建脚本模版'}
      size={1280}
      loading={is_detail_fetching}
      onClose={handle_close}
      footer={
        <div className="flex items-center">
          <div className="flex-auto"></div>
          <Space>
            <Button variant="outlined" color="default" onClick={handle_close}>
              取消
            </Button>
            <Button
              variant="filled"
              color="primary"
              loading={is_submitting}
              onClick={onSubmit}
            >
              保存
            </Button>
          </Space>
        </div>
      }
    >
      <Form layout="vertical" className="pb-4">
        <div className="flex items-start gap-x-4">
          <FormControl
            name="video_url"
            control={control}
            label="视频"
            required
            render={({ field }) => (
              <Upload<UploadFileOutputDto>
                id="videoUploader"
                accept="video/*"
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({ accept: 'video/*' }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                maxCount={1}
                listType="picture-card"
                fileList={field.value}
                disabled={is_detail_fetching || is_submitting}
                onPreview={onPreview}
                onChange={(info) => {
                  if (info.file.status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `视频 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(info.fileList);
                }}
              >
                {!field.value.length && (
                  <div>
                    <PlusOutlined />
                    <div className="mt-2">上传视频</div>
                  </div>
                )}
              </Upload>
            )}
          />
          <FormControl
            name="reference_image_urls"
            control={control}
            label="参考图"
            render={({ field }) => (
              <Upload<UploadFileOutputDto>
                id="imageUploader"
                multiple
                maxCount={5}
                accept="image/*"
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({ accept: 'image/*' }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                listType="picture-card"
                fileList={field.value ?? void 0}
                disabled={is_detail_fetching || is_submitting}
                showUploadList={{
                  showPreviewIcon: true,
                  showRemoveIcon: true,
                }}
                onPreview={onPreview}
                onChange={(info) => {
                  if (info.file.status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `图片 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(info.fileList);
                }}
              >
                <div>
                  <PlusOutlined />
                  <div className="mt-2">上传图片</div>
                </div>
              </Upload>
            )}
          />
        </div>
        {is_edit && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <FormControl
              control={control}
              name="video_duration"
              label="视频时长"
              render={({ field }) => {
                return (
                  <InputNumber
                    classNames={{ root: '!w-full' }}
                    {...field}
                    min={0}
                    precision={0}
                    suffix="秒"
                  ></InputNumber>
                );
              }}
            ></FormControl>
            <FormControl
              control={control}
              name="video_aspect_ratio"
              label="视频宽高比"
              render={({ field }) => {
                return (
                  <AspectRatioSelect
                    allowClear={false}
                    {...field}
                  ></AspectRatioSelect>
                );
              }}
            ></FormControl>
          </div>
        )}

        <FormControl
          name="product_name"
          control={control}
          label="产品名称"
          required
          render={({ field }) => (
            <Input
              {...field}
              value={field.value}
              disabled={is_detail_fetching || is_submitting}
            />
          )}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <FormControl
            name="tags"
            control={control}
            label="标签"
            render={({ field }) => (
              <Select<string>
                mode="tags"
                maxTagCount="responsive"
                value={field.value}
                disabled={is_detail_fetching || is_submitting}
                onChange={(values) => {
                  field.onChange(values);
                }}
                options={tags.map((item) => ({
                  label: item.name,
                  value: String(item.id),
                }))}
              />
            )}
          />
          <FormControl
            name="hook_type_id"
            control={control}
            label="Hook 类型"
            render={({ field }) => (
              <HookTypeSelect
                allowClear
                value={field.value}
                disabled={is_detail_fetching || is_submitting}
                onChange={field.onChange}
              />
            )}
          />
          <FormControl
            name="script_language_id"
            control={control}
            label="脚本语言"
            render={({ field }) => (
              <ScriptLanguageSelect
                allowClear
                value={field.value}
                disabled={is_detail_fetching || is_submitting}
                onChange={field.onChange}
              />
            )}
          />
          <FormControl
            name="selling_point_ids"
            control={control}
            label="卖点类型"
            render={({ field }) => (
              <SellingPointSelect
                mode="multiple"
                maxTagCount="responsive"
                value={field.value}
                disabled={is_detail_fetching || is_submitting}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <FormControl
          name="prompt"
          control={control}
          label="脚本详情"
          required
          render={({ field }) => (
            <Input.TextArea
              {...field}
              value={field.value}
              autoSize={{ minRows: 20 }}
              disabled={is_detail_fetching || is_submitting}
            />
          )}
        />

        <FormControl
          name="card_result"
          control={control}
          label="抽卡情况"
          render={({ field }) => (
            <Input.TextArea
              {...field}
              value={field.value ?? ''}
              rows={3}
              disabled={is_detail_fetching || is_submitting}
            />
          )}
        />

        <FormControl
          name="remark"
          control={control}
          label="备注"
          render={({ field }) => (
            <Input.TextArea
              {...field}
              value={field.value ?? ''}
              rows={3}
              disabled={is_detail_fetching || is_submitting}
            />
          )}
        />
      </Form>
    </AiDrawer>
  );
};
